import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  MotorcycleIcon,
  HashIcon,
  PackageIcon,
  DocumentIcon,
  SettingsIcon,
  ChevronDownIcon,
  UserIcon,
} from "../lib/icons.js";

/**
 * Kompaktní top navigace s dropdown (megamenu) pro seskupené kategorie.
 *
 * Struktura (4 položky místo 10):
 *   1. Model                 — rychlý odkaz
 *   2. # Číslo dílu          — rychlý odkaz
 *   3. Katalog ▼             — dropdown (2 sloupce): KTM × Dodavatelé
 *   4. Provoz ▼              — dropdown: Zdroje KTM, Nabídky, Sklady, FTP
 */

export interface CatalogGroup {
  label: string;
  items: Array<{ to: string; label: string; hint?: string }>;
}

/**
 * ⚠ Stejný seznam používá i mobilní menu v `App.tsx` (`MobileMenu`). Proto je
 * exportovaný — dvě kopie by znamenaly, že nový dodavatel přibude jen na
 * počítači a na telefonu ne.
 *
 * 🔑 Dělení podle toho, ODKUD DATA BEROU — Vít 15. 8. 2026. Není to kosmetika:
 * u oficiálního feedu ručí za obsah dodavatel a když se něco rozbije, řeší se
 * to s ním. U vlastního sběru ručíme za obsah my a rozbije se to tiše, když
 * dodavatel překope stránku. Nový dodavatel patří do té skupiny, ze které
 * opravdu čte — ⛔ ne tam, kam se hodí abecedně.
 */
export const CATALOG: CatalogGroup[] = [
  {
    label: "KTM katalog",
    items: [
      { to: "/powerparts", label: "PowerParts", hint: "Technické díly a tuning" },
      { to: "/powerwear", label: "PowerWear", hint: "Oblečení a doplňky" },
      { to: "/collections", label: "Collections", hint: "Kolekce a sady" },
    ],
  },
  {
    label: "Feed od dodavatele",
    items: [
      { to: "/mefo", label: "MEFO", hint: "Acerbis, Polisport, MITAKA, …" },
      { to: "/b2bike", label: "B2Bike", hint: "Spidi, Cardo, Hepco Becker, …" },
      { to: "/garmin", label: "Garmin", hint: "GPS navigace — import zatím vypnutý" },
    ],
  },
  {
    label: "Sbíráme si sami",
    items: [
      { to: "/motorex", label: "MOTOREX", hint: "Oleje, maziva, brzdové kapaliny" },
      { to: "/risk", label: "RISK RACING", hint: "Startovací brány, stojany, kanystry" },
      { to: "/azpneu", label: "AZ PNEU", hint: "Moto pneumatiky — Michelin, Mitas" },
      { to: "/adrenalin", label: "ADRENALIN-OIL", hint: "FunnelWeb filtry, Řídítka ACTION POWER" },
      { to: "/sg12", label: "GAERNE", hint: "Cross / Enduro boty — dovozce sg12.cz" },
      { to: "/angelswheels", label: "ANGEL'S WHEELS", hint: "Kola Kite Elite / G2 ProSeries a díly kol" },
      { to: "/ohlins", label: "ÖHLINS", hint: "Rozkresy tlumičů a vidlic — klikací výkresy" },
    ],
  },
];

export const PROVOZ = [
  { to: "/admin/zdroje", label: "Zdroje KTM", icon: <DocumentIcon size={16} />, hint: "Odkud která informace je — a kde NENÍ" },
  { to: "/admin/automations", label: "Automatizace", icon: <SettingsIcon size={16} />, hint: "Naši roboti — stav a poslední běhy" },
  { to: "/quotes", label: "Nabídky", icon: <DocumentIcon size={16} />, hint: "Uložené košíky mechanika" },
  { to: "/admin/customers", label: "Klienti", icon: <UserIcon size={16} />, hint: "Zákaznická databáze (B2C/B2B)" },
  { to: "/admin/b2bike", label: "B2Bike sync", icon: <SettingsIcon size={16} />, hint: "Stav importu B2Bike" },
  { to: "/admin/integrations", label: "Napojení", icon: <SettingsIcon size={16} />, hint: "Stav API, FTP, importérů" },
  { to: "/admin/warehouses", label: "Sklady", icon: <PackageIcon size={16} />, hint: "Stavy a sync" },
  { to: "/admin/ftp", label: "FTP", icon: <SettingsIcon size={16} />, hint: "Ceníky a importy" },
];

// Cesty, při kterých se "Katalog" / "Provoz" považuje za aktivní.
const CATALOG_PATHS = ["/powerparts", "/powerwear", "/collections", "/mefo", "/motorex", "/risk", "/garmin", "/b2bike", "/azpneu", "/adrenalin", "/sg12", "/angelswheels"];
const PROVOZ_PATHS = ["/quotes", "/quote/", "/admin/"];

/**
 * ⛔ Pod 640 px se tahle navigace NEZOBRAZUJE — tam jede `MobileMenu`
 * v `App.tsx`. Důvod je změřený (15. 8. 2026): rozbalovací panely jsou
 * kotvené k tlačítku, ne k obrazovce, takže „Katalog" končil 67 px
 * a „Provoz" 111 px za pravým okrajem a rozjížděly vodorovný posuvník
 * na KAŽDÉ stránce. Ukotvit je na mobilu nešlo — „Katalog" je široký
 * 339 px a jeho tlačítko sedí 84 px od kraje, takže se panel nevejde
 * ani doleva, ani doprava.
 */
export function NavMenu(): JSX.Element {
  return (
    <nav className="hidden sm:flex items-center gap-0.5 sm:gap-1 text-sm min-w-0">
      <QuickLink to="/search/model" icon={<MotorcycleIcon size={16} />}>
        Model
      </QuickLink>
      <QuickLink to="/search/part" icon={<HashIcon size={16} />}>
        Číslo dílu
      </QuickLink>
      <CatalogDropdown />
      <ProvozDropdown />
    </nav>
  );
}

function QuickLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      title={typeof children === "string" ? children : undefined}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md transition-colors ${
          isActive
            ? "bg-white/10 text-white"
            : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`
      }
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
    </NavLink>
  );
}

function CatalogDropdown(): JSX.Element {
  const { pathname } = useLocation();
  const active = CATALOG_PATHS.some((p) => pathname.startsWith(p));
  const { open, ref, triggerProps, panelProps } = useDropdown();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        {...triggerProps}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
          active || open
            ? "bg-white/10 text-white"
            : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <PackageIcon size={16} />
        <span>Katalog</span>
        <ChevronDownIcon size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {/*
        Šířka panelu je stropovaná viewportem — napevno 520 px přetékalo
        na mobilu přes okraj obrazovky. Sloupce se pod 640 px srovnají pod sebe.
      */}
      {open && (
        <div
          {...panelProps}
          className="absolute left-0 top-full mt-2 w-[min(520px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto rounded-xl border border-zinc-200 bg-white text-zinc-900 shadow-lg p-4 z-50"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {CATALOG.map((group) => (
              <div key={group.label}>
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-2">
                  {group.label}
                </div>
                <ul className="space-y-1">
                  {group.items.map((i) => (
                    <li key={i.to}>
                      <NavLink
                        to={i.to}
                        onClick={panelProps.onItemClick}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-md transition-colors ${
                            isActive
                              ? "bg-ktm-orange/10 text-ktm-orange"
                              : "hover:bg-zinc-50"
                          }`
                        }
                      >
                        <div className="font-medium">{i.label}</div>
                        {i.hint && (
                          <div className="text-xs text-zinc-500 mt-0.5">{i.hint}</div>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProvozDropdown(): JSX.Element {
  const { pathname } = useLocation();
  const active = PROVOZ_PATHS.some((p) => pathname.startsWith(p));
  const { open, ref, triggerProps, panelProps } = useDropdown();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        {...triggerProps}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
          active || open
            ? "bg-white/10 text-white"
            : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <SettingsIcon size={16} />
        <span>Provoz</span>
        <ChevronDownIcon size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          {...panelProps}
          className="absolute left-0 top-full mt-2 min-w-[260px] rounded-xl border border-zinc-200 bg-white text-zinc-900 shadow-lg p-2 z-50"
        >
          <ul className="space-y-0.5">
            {PROVOZ.map((i) => (
              <li key={i.to}>
                <NavLink
                  to={i.to}
                  onClick={panelProps.onItemClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      isActive
                        ? "bg-ktm-orange/10 text-ktm-orange"
                        : "hover:bg-zinc-50"
                    }`
                  }
                >
                  <span className="text-zinc-400">{i.icon}</span>
                  <div>
                    <div className="font-medium">{i.label}</div>
                    {i.hint && (
                      <div className="text-xs text-zinc-500">{i.hint}</div>
                    )}
                  </div>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Helper hook — spravuje open state + click-outside + Escape + hover delay. */
function useDropdown(): {
  open: boolean;
  ref: React.RefObject<HTMLDivElement>;
  triggerProps: {
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    "aria-expanded": boolean;
    "aria-haspopup": "true";
  };
  panelProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onItemClick: () => void;
  };
} {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const cancelClose = (): void => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = (): void => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return {
    open,
    ref,
    triggerProps: {
      onClick: () => setOpen((v) => !v),
      onMouseEnter: () => {
        cancelClose();
        setOpen(true);
      },
      onMouseLeave: scheduleClose,
      "aria-expanded": open,
      "aria-haspopup": "true",
    },
    panelProps: {
      onMouseEnter: cancelClose,
      onMouseLeave: scheduleClose,
      onItemClick: () => setOpen(false),
    },
  };
}
